import type { ReactNode } from 'react'
import { Box, Stack, Tooltip, Typography, alpha, keyframes } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { MetricState } from './index'

type MetricStateTone = 'active' | 'neutral' | 'info' | 'error'

export type MetricStatePresentation = {
  label: string
  title: string
  description: string
  tone: MetricStateTone
  active?: boolean
  icon?: ReactNode
}

export type MetricStateIndicatorVariant = 'inline' | 'block' | 'chip'

type MetricStateIndicatorProps = {
  state?: MetricState | null
  variant?: MetricStateIndicatorVariant
  label?: ReactNode
  message?: ReactNode
  minHeight?: number
  showLabel?: boolean
  sx?: SxProps<Theme>
}

type MetricValueFallbackProps = {
  state?: MetricState | null
  fallback: ReactNode
  minHeight?: number
  sx?: SxProps<Theme>
}

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`

const PRESENTATION: Partial<Record<MetricState, MetricStatePresentation>> = {
  [MetricState.NO_RUN]: {
    icon: <HourglassEmptyOutlinedIcon fontSize="small" />,
    label: 'Not run',
    title: 'Not yet calculated',
    description: 'This metric has not been processed yet.',
    tone: 'neutral',
  },
  [MetricState.QUEUED]: {
    label: 'Queued',
    title: 'Queued for calculation',
    description: 'This metric is waiting to be calculated.',
    tone: 'active',
    active: true,
  },
  [MetricState.RUNNING]: {
    label: 'Calculating',
    title: 'Calculating',
    description: 'This metric is being calculated.',
    tone: 'active',
    active: true,
  },
  [MetricState.NO_DATA]: {
    icon: <InfoOutlinedIcon fontSize="small" />,
    label: 'No data',
    title: 'No data available',
    description: 'This metric was processed but produced no result for this location.',
    tone: 'info',
  },
  [MetricState.FAILED]: {
    icon: <ErrorOutlineIcon fontSize="small" />,
    label: 'Failed',
    title: 'Calculation failed',
    description: 'Something went wrong while processing this metric.',
    tone: 'error',
  },
}

export function getMetricStatePresentation(state?: MetricState | null): MetricStatePresentation | null {
  return state ? PRESENTATION[state] ?? null : null
}

export function getMostRelevantMetricState(states: Array<MetricState | null | undefined>): MetricState | null {
  const values = states.filter((state): state is MetricState => Boolean(state))
  if (!values.length) return null

  if (values.includes(MetricState.RUNNING)) return MetricState.RUNNING
  if (values.includes(MetricState.QUEUED)) return MetricState.QUEUED
  if (values.includes(MetricState.FAILED)) return MetricState.FAILED
  if (values.includes(MetricState.NO_DATA)) return MetricState.NO_DATA
  if (values.includes(MetricState.NO_RUN)) return MetricState.NO_RUN
  if (values.every(state => state === MetricState.SUCCEEDED)) return MetricState.SUCCEEDED

  return values.find(state => state !== MetricState.SUCCEEDED) ?? MetricState.SUCCEEDED
}

function toneColor(theme: Theme, tone: MetricStateTone) {
  switch (tone) {
    case 'active':
      return theme.palette.primary.main
    case 'error':
      return theme.palette.error.main
    case 'info':
      return theme.palette.info.main
    case 'neutral':
    default:
      return theme.palette.text.secondary
  }
}

function AnimatedDots({ variant }: { variant: MetricStateIndicatorVariant }) {
  const size = variant === 'block' ? 8 : 5
  return (
    <Box aria-hidden sx={{ display: 'inline-flex', alignItems: 'center', gap: variant === 'block' ? 0.75 : 0.5 }}>
      {[0, 1, 2].map(index => (
        <Box
          key={index}
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: 'currentColor',
            animation: `${pulse} 1.2s ease-in-out infinite`,
            animationDelay: `${index * 0.16}s`,
          }}
        />
      ))}
    </Box>
  )
}

const MetricStateIndicator = ({
  state,
  variant = 'inline',
  label,
  message,
  minHeight = 80,
  showLabel,
  sx,
}: MetricStateIndicatorProps) => {
  const presentation = getMetricStatePresentation(state)
  if (!presentation) return null

  const resolvedLabel = label === undefined ? presentation.label : label
  const resolvedMessage = message !== undefined ? message : presentation.description
  const labelVisible = showLabel ?? Boolean(resolvedLabel)
  const marker = presentation.active ? (
    <AnimatedDots variant={variant} />
  ) : (
    <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      {presentation.icon}
    </Box>
  )

  if (variant === 'block') {
    return (
      <Stack
        role="status"
        aria-live={presentation.active ? 'polite' : undefined}
        spacing={0.75}
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={[
          theme => ({
            minHeight,
            width: '100%',
            py: 2,
            px: 1,
            color: toneColor(theme, presentation.tone),
          }),
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {marker}
        <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
          {label === undefined ? presentation.title : resolvedLabel}
        </Typography>
        {resolvedMessage ? (
          <Typography variant="caption" color="text.secondary">
            {resolvedMessage}
          </Typography>
        ) : null}
      </Stack>
    )
  }

  const inline = (
    <Stack
      component="span"
      role="status"
      aria-live={presentation.active ? 'polite' : undefined}
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={[
        theme => ({
          display: 'inline-flex',
          width: 'fit-content',
          maxWidth: '100%',
          minWidth: 0,
          color: toneColor(theme, presentation.tone),
          ...(variant === 'chip'
            ? {
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                border: `1px solid ${alpha(toneColor(theme, presentation.tone), 0.28)}`,
                backgroundColor: alpha(toneColor(theme, presentation.tone), 0.08),
              }
            : {}),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {marker}
      {labelVisible ? (
        <Typography component="span" variant="caption" noWrap sx={{ color: 'inherit', minWidth: 0, lineHeight: 1.35 }}>
          {resolvedLabel}
        </Typography>
      ) : null}
    </Stack>
  )

  return (
    <Tooltip title={resolvedMessage} arrow enterTouchDelay={0}>
      {inline}
    </Tooltip>
  )
}

export function MetricValueFallback({ state, fallback, minHeight = 88, sx }: MetricValueFallbackProps) {
  if (state && state !== MetricState.SUCCEEDED) {
    return <MetricStateIndicator state={state} variant="block" minHeight={minHeight} sx={sx} />
  }

  return (
    <Typography variant="body2" color="text.secondary">
      {fallback}
    </Typography>
  )
}

export default MetricStateIndicator
